import { CmsContent, CmsTemplateParams } from './types';
// eslint-disable-next-line import/no-named-as-default
import DOMPurify, { Config } from 'dompurify';

export class CmsContentResolver {
    private static readonly sanitizeConfig: Config = {
        ALLOWED_TAGS: ['p', 'li', 'ul', 'ol', 'b', 'i', 'u', 'sub', 'sup', 'a', 'br', 'span'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
    };

    /**
     * Resolves CMS content by:
     * 1. Interpolating variables using {{variableName}} syntax
     * 2. Sanitize HTML
     * 3. Replacing custom link syntax [[label|url]] with <a> tags
     */
    static resolve(content: CmsContent, params: CmsTemplateParams): CmsContent {
        return this.processNode(content, params);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static processNode(node: unknown, params: CmsTemplateParams): any {
        if (typeof node === 'string') {
            const withVariables = this.interpolateVariables(node, params);
            const sanitized = DOMPurify.sanitize(withVariables, this.sanitizeConfig);

            return this.parseLinks(sanitized);
        }

        if (Array.isArray(node)) {
            return node.map((item) => this.processNode(item, params));
        }

        if (node && typeof node === 'object') {
            const result: Record<string, unknown> = {};

            for (const key in node) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                result[key] = this.processNode((node as any)[key], params);
            }

            return result;
        }

        return node;
    }

    /**
     * Replaces variable placeholders using syntax:
     *
     *   {{variableName}}
     *
     * Example:
     *   "Hello {{userName}}"
     */
    private static interpolateVariables(input: string, params: CmsTemplateParams): string {
        if (!params.length) {
            return input;
        }

        const VARIABLE_REGEX = /{{\s*([\w.]+)\s*}}/g;

        return input.replace(VARIABLE_REGEX, (match: string, currentKey: string) => {
            const param = params.find(({ key }) => key === currentKey);

            if (param) {
                return param.value;
            }

            // leave untouched if not provided
            return match;
        });
    }

    /**
     * Parse custom link syntax: [[label|url]]
     *
     * Allowed URLs:
     *   - Internal: /page
     *   - External: https://...
     *   - Email: mailto:someone@example.com
     *   - Phone: tel:+123456789
     *
     * Example:
     *   "Click [[here|/profile]]"
     *
     * Becomes:
     *   "Click <a href="/profile" target="_blank" rel="noopener noreferrer">here</a>"
     */
    private static parseLinks(input: string): string {
        const LINK_REGEX = /\[\[\s*([^|\]]+)\s*\|\s*([^\]]+)\s*\]\]/g;

        return input.replace(LINK_REGEX, (match: string, rawLabel: string, rawUrl: string) => {
            const label = rawLabel.trim();
            const url = rawUrl.trim();

            if (!this.isSafeUrl(url)) {
                return match; // keep original if unsafe
            }

            const safeLabel = DOMPurify.sanitize(label, this.sanitizeConfig);
            const safeUrl = this.escapeAttribute(url);

            const target = safeUrl.startsWith('/') ? '_self' : '_blank';

            return `<a href="${safeUrl}" target="${target}" rel="noopener noreferrer">${safeLabel}</a>`;
        });
    }

    /** Only allow safe URLs: internal, https, mailto, tel */
    private static isSafeUrl(url: string): boolean {
        try {
            // Allow relative URLs
            if (url.startsWith('/')) {
                return true;
            }

            const parsed = new URL(url);

            // Allow only specific protocols
            return parsed.protocol === 'https:' || parsed.protocol === 'mailto:' || parsed.protocol === 'tel:';
        } catch {
            return false;
        }
    }

    private static escapeAttribute(value: string): string {
        return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
}
